import type { InferResponse } from '@/hooks/useApiClient'
import { getLocaleSetting } from '@/utils/intl/language'
import type { SSOIdentityProvider } from '@gravitate-js/excalibrr/dist'
import { useQuery } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'

export type SetupData = InferResponse<'/setup'> & {
  ssoProviders: SSOIdentityProvider[]
  ff_order_grid_row_limit?: boolean
  ff_site_monitoring_ssrm?: boolean
  ff_admin_sites_ssrm?: boolean
  ff_freight_grid_row_limit?: boolean
  carrier_dispatch_require_confirmation_on_bulk_send?: boolean
  ff_not_delivered_orders?: boolean
  ff_tank_wagons?: boolean
  psp_enabled?: boolean
  valuation_enabled?: boolean
  demand_model_available?: boolean
  buy_plan_enabled?: boolean
  ltl_model_available?: boolean
  scout_enabled?: boolean
}

export function useSetup() {
  return useQuery<SetupData>({
    staleTime: Number.POSITIVE_INFINITY,
    queryKey: ['setup'],
    queryFn: async () => {
      const baseUrl = getAPIURL()
      const resp = await fetch(`${baseUrl}setup`, {
        headers: {
          'Accept-Language': getLocaleSetting(),
        },
      })
      if (!resp.ok) {
        throw new Error('Network response was not ok')
      }
      const json = await resp.json()
      json.ssoProviders = getSSOProviders(json)

      return json
    },
  })
}

export function useNotDeliveredEnabled() {
  const setup = useSetup()
  return setup.data?.ff_not_delivered_orders ?? false
}

export function useValuationEnabled() {
  const setup = useSetup()
  return setup.data?.valuation_enabled ?? false
}

export function useScoutEnabled() {
  const setup = useSetup()
  return setup.data?.scout_enabled ?? false
}

function getSSOProviders(data: SetupData): SSOIdentityProvider {
  return data?.sso_providers?.map((p) => ({
    IdentityProviderId: p.provider,
    Name: p.display_name,
    ClientId: p.client_id,
    Provider: p.provider,
    RedirectUri: p.redirect_uri,
    AuthorizeUrl: p.authorization_url,
    Scopes: p.scopes.join(' '),
  }))
}
